/**
 * PanelResizeManager - Unified panel resizing system
 * 
 * Provides consistent resizing behavior across all panels
 * Features:
 * - Vertical and horizontal resizing
 * - Size persistence in localStorage
 * - Min/max constraints
 * - Smooth animations
 * - Accessibility support
 * 
 * @module utils/PanelResize
 */

export class PanelResizeManager {
  constructor() {
    this.panels = new Map();
    this.observers = new Map();
    this.activeResize = null;
  }
  
  /**
   * Register a panel for resizing
   * @param {string} panelId - DOM element ID
   * @param {Object} options - Configuration options
   */
  register(panelId, options = {}) {
    const defaults = {
      direction: 'vertical', // vertical | horizontal
      min: 200,
      max: '85vh',
      persist: true,
      handle: 'bottom', // bottom | right | left | top
      onResize: null,
      smoothResize: true
    };
    
    const config = { ...defaults, ...options };
    const panel = document.getElementById(panelId);
    
    if (!panel) {
      console.warn(`Panel ${panelId} not found`);
      return;
    }
    
    // Add resize class for styling
    panel.classList.add('resizable-panel');
    panel.classList.add(`resizable-${config.direction}`);
    
    // Create resize handle
    const handle = this.createHandle(config);
    panel.appendChild(handle);
    
    // Restore saved size
    if (config.persist) {
      const saved = localStorage.getItem(`panel-size-${panelId}`);
      if (saved) {
        const dimension = config.direction === 'vertical' ? 'height' : 'width';
        panel.style[dimension] = saved;
      }
    }
    
    // Setup resize observer for container changes
    if (window.ResizeObserver) {
      const observer = new ResizeObserver(entries => {
        if (config.onResize) {
          config.onResize(entries[0].contentRect);
        }
      });
      
      observer.observe(panel);
      this.observers.set(panelId, observer);
    }
    
    // Store config
    this.panels.set(panelId, { panel, config, handle });
    
    // Attach resize behavior
    this.attachResizeBehavior(panelId);
  }
  
  /**
   * Create resize handle element
   */
  createHandle(config) {
    const handle = document.createElement('div');
    handle.className = `resize-handle resize-handle-${config.handle}`;
    handle.setAttribute('role', 'separator');
    handle.setAttribute('aria-orientation', config.direction);
    handle.setAttribute('tabindex', '0');
    
    // Add visual grip
    const grip = document.createElement('div');
    grip.className = config.direction === 'vertical' ? 
      'resize-grip-horizontal' : 'resize-grip-vertical';
    handle.appendChild(grip);
    
    // Add hover effect
    handle.addEventListener('mouseenter', () => {
      handle.classList.add('resize-handle-hover');
    });
    
    handle.addEventListener('mouseleave', () => {
      if (!this.activeResize) {
        handle.classList.remove('resize-handle-hover');
      }
    });
    
    return handle;
  }
  
  /**
   * Attach resize behavior to panel
   */
  attachResizeBehavior(panelId) {
    const { panel, config, handle } = this.panels.get(panelId);
    let startPos = 0;
    let startSize = 0;
    
    const startResize = (e) => {
      e.preventDefault();
      this.activeResize = panelId;
      
      startPos = config.direction === 'vertical' ? e.clientY : e.clientX;
      startSize = config.direction === 'vertical' ? 
        panel.offsetHeight : panel.offsetWidth;
      
      document.body.style.userSelect = 'none';
      document.body.style.cursor = config.direction === 'vertical' ? 
        'row-resize' : 'col-resize';
      
      panel.classList.add('panel-resizing');
      handle.classList.add('resize-handle-active');
      
      // Add global listeners
      document.addEventListener('pointermove', handleResize);
      document.addEventListener('pointerup', endResize);
      document.addEventListener('pointercancel', endResize);
    };
    
    const handleResize = (e) => {
      if (this.activeResize !== panelId) return;
      
      const currentPos = config.direction === 'vertical' ? e.clientY : e.clientX;
      const diff = currentPos - startPos;
      
      // Calculate new size based on handle position
      let newSize = startSize;
      if (config.handle === 'bottom' || config.handle === 'right') {
        newSize = startSize + diff;
      } else {
        newSize = startSize - diff;
      }
      
      // Apply constraints
      const min = this.parseSize(config.min, panel.parentElement);
      const max = this.parseSize(config.max, panel.parentElement);
      const constrainedSize = Math.max(min, Math.min(max, newSize));
      
      // Apply new size with smooth transition
      const dimension = config.direction === 'vertical' ? 'height' : 'width';
      
      if (config.smoothResize) {
        requestAnimationFrame(() => {
          panel.style[dimension] = `${constrainedSize}px`;
        });
      } else {
        panel.style[dimension] = `${constrainedSize}px`;
      }
      
      // Callback
      if (config.onResize) {
        config.onResize({
          width: panel.offsetWidth,
          height: panel.offsetHeight,
          size: constrainedSize
        });
      }
    };
    
    const endResize = () => {
      if (this.activeResize !== panelId) return;
      
      this.activeResize = null;
      
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      
      panel.classList.remove('panel-resizing');
      handle.classList.remove('resize-handle-active');
      handle.classList.remove('resize-handle-hover');
      
      // Remove global listeners
      document.removeEventListener('pointermove', handleResize);
      document.removeEventListener('pointerup', endResize);
      document.removeEventListener('pointercancel', endResize);
      
      // Persist size
      if (config.persist) {
        const dimension = config.direction === 'vertical' ? 'height' : 'width';
        const size = panel.style[dimension];
        localStorage.setItem(`panel-size-${panelId}`, size);
      }
    };
    
    // Attach to handle
    handle.addEventListener('pointerdown', startResize);
    
    // Keyboard support
    handle.addEventListener('keydown', (e) => {
      const step = 10;
      let newSize = config.direction === 'vertical' ? 
        panel.offsetHeight : panel.offsetWidth;
      
      switch(e.key) {
        case 'ArrowUp':
        case 'ArrowLeft':
          newSize -= step;
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          newSize += step;
          break;
        default:
          return;
      }
      
      e.preventDefault();
      
      const min = this.parseSize(config.min, panel.parentElement);
      const max = this.parseSize(config.max, panel.parentElement);
      const constrainedSize = Math.max(min, Math.min(max, newSize));
      
      const dimension = config.direction === 'vertical' ? 'height' : 'width';
      panel.style[dimension] = `${constrainedSize}px`;
      
      if (config.persist) {
        localStorage.setItem(`panel-size-${panelId}`, panel.style[dimension]);
      }
    });
  }
  
  /**
   * Parse size value (handles px, vh, vw, %)
   */
  parseSize(size, container) {
    if (typeof size === 'number') return size;
    if (typeof size !== 'string') return 200;
    
    if (size.endsWith('vh')) {
      return window.innerHeight * parseFloat(size) / 100;
    }
    if (size.endsWith('vw')) {
      return window.innerWidth * parseFloat(size) / 100;
    }
    if (size.endsWith('%') && container) {
      const dimension = this.activeResize ? 
        (this.panels.get(this.activeResize).config.direction === 'vertical' ? 
          container.offsetHeight : container.offsetWidth) :
        container.offsetHeight;
      return dimension * parseFloat(size) / 100;
    }
    return parseFloat(size) || 200;
  }
  
  /**
   * Unregister a panel
   */
  unregister(panelId) {
    const data = this.panels.get(panelId);
    if (!data) return;
    
    // Clean up observer
    const observer = this.observers.get(panelId);
    if (observer) {
      observer.disconnect();
      this.observers.delete(panelId);
    }
    
    // Remove handle
    data.handle?.remove();
    
    // Remove classes
    data.panel.classList.remove('resizable-panel');
    data.panel.classList.remove(`resizable-${data.config.direction}`);
    
    // Clear from registry
    this.panels.delete(panelId);
  }
  
  /**
   * Get current size of panel
   */
  getSize(panelId) {
    const data = this.panels.get(panelId);
    if (!data) return null;
    
    return {
      width: data.panel.offsetWidth,
      height: data.panel.offsetHeight
    };
  }
  
  /**
   * Set size programmatically
   */
  setSize(panelId, size) {
    const data = this.panels.get(panelId);
    if (!data) return;
    
    const dimension = data.config.direction === 'vertical' ? 'height' : 'width';
    data.panel.style[dimension] = typeof size === 'number' ? `${size}px` : size;
    
    if (data.config.persist) {
      localStorage.setItem(`panel-size-${panelId}`, data.panel.style[dimension]);
    }
    
    if (data.config.onResize) {
      data.config.onResize({
        width: data.panel.offsetWidth,
        height: data.panel.offsetHeight,
        size
      });
    }
  }
  
  /**
   * Reset panel to default size
   */
  reset(panelId) {
    const data = this.panels.get(panelId);
    if (!data) return;
    
    const dimension = data.config.direction === 'vertical' ? 'height' : 'width';
    data.panel.style[dimension] = '';
    
    if (data.config.persist) {
      localStorage.removeItem(`panel-size-${panelId}`);
    }
  }
  
  /**
   * Destroy all panels and clean up
   */
  destroy() {
    this.panels.forEach((_, panelId) => {
      this.unregister(panelId);
    });
  }
}

// Export singleton instance
export const panelResize = new PanelResizeManager();

// Export for debugging in dev mode
if (import.meta.env?.DEV) {
  window.panelResize = panelResize;
  console.log('🔧 PanelResizeManager available at window.panelResize');
}

export default panelResize;
